import { useCallback, useEffect, useState } from 'react';

import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { IConsiderPlan } from 'APIPackage/PlanListModule/interfaces/IConsiderPlan';
import { IChangePlanStatusModalValues } from 'CorePackage/ChangePlanStatusModalsModule/interfaces/IChangePlanStatusModalValues';

import { ChangePlanStatusTypesEnum } from '../enums/ChangePlanStatusTypesEnum';
import { IChangePlanStatusToConsiderModalForm } from '../interfaces/IChangePlanStatusToConsiderModalForm';
import { ChangePlanStatusModal } from './ChangePlanStatusModal';
import { ChangePlanStatusToConsiderModal } from './ChangePlanStatusToConsiderModal';
import { ChangePlanStatusConfirmModal } from './ChangePlanStatusConfirmModal';
import { ChangePlanStatusErrorModal } from './ChangePlanStatusErrorModal';

interface IChangePlanStatusModalsProps {
  activeModal: ChangePlanStatusTypesEnum | null;
  planStatus: ErknmPlanStatusesEnum | undefined;
  errorText?: string;
  onChangeApply: (data: IChangePlanStatusModalValues) => void;
  onChangeToConsiderApply: (data: IConsiderPlan) => void;
  onClose: () => void;
}

export const ChangePlanStatusModals = ({
  activeModal,
  planStatus,
  errorText,
  onChangeApply,
  onChangeToConsiderApply,
  onClose,
}: IChangePlanStatusModalsProps) => {
  const [isChangePlanStatusModalVisible, setIsChangePlanStatusModalVisible] = useState<boolean>(false);
  const [isChangePlanStatusToConsiderModalVisible, setIsChangePlanStatusToConsiderModalVisible] = useState<boolean>(
    false,
  );
  const [isChangePlanStatusErrorModalVisible, setIsChangePlanStatusErrorModalVisible] = useState<boolean>(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState<boolean>(false);
  const [
    changePlanStatusToConsiderModalValues,
    setChangePlanStatusToConsiderModalValues,
  ] = useState<IChangePlanStatusToConsiderModalForm>();

  useEffect(() => {
    if (activeModal === ChangePlanStatusTypesEnum.PRIMARY) {
      setIsConfirmModalVisible(true);
    }
    if (activeModal === ChangePlanStatusTypesEnum.TO_CONSIDER) {
      setIsChangePlanStatusToConsiderModalVisible(true);
    }
    if (activeModal === ChangePlanStatusTypesEnum.ERROR) {
      setIsChangePlanStatusErrorModalVisible(true);
    }
  }, [activeModal]);

  const handleChangePlanStatusApply = useCallback(
    (values: IChangePlanStatusModalValues) => {
      setIsChangePlanStatusModalVisible(false);
      onChangeApply(values);
    },
    [onChangeApply],
  );

  const handleChangePlanStatusToConsiderApply = useCallback((values: IChangePlanStatusToConsiderModalForm) => {
    setChangePlanStatusToConsiderModalValues(values);
    setIsChangePlanStatusToConsiderModalVisible(false);
    setIsConfirmModalVisible(true);
  }, []);

  const handleChangePlanStatusModalClose = useCallback(() => {
    setIsChangePlanStatusModalVisible(false);
    onClose();
  }, [onClose]);

  const handleChangePlanStatusToConsiderModalClose = useCallback(() => {
    setIsChangePlanStatusToConsiderModalVisible(false);
    onClose();
  }, [onClose]);

  const handleConfirmModalApply = useCallback(() => {
    if (activeModal === ChangePlanStatusTypesEnum.PRIMARY) {
      setIsConfirmModalVisible(false);
      setIsChangePlanStatusModalVisible(true);
    }
    if (activeModal === ChangePlanStatusTypesEnum.TO_CONSIDER && changePlanStatusToConsiderModalValues) {
      const { fullName, position, document } = changePlanStatusToConsiderModalValues;

      if (fullName && position && document) {
        onChangeToConsiderApply({
          fullNameWhoConsideredPlan: fullName,
          positionWhoConsideredPlan: position,
          attachmentDocument: {
            guid: null,
            dateCreate: null,
            fileName: document.fileName,
            filePath: document.filePath || null,
            fileSize: document.fileSize,
            mimeType: document.mimeType || null,
            bucketName: document.bucketName || null,
            correlationToken: null,
            hash: null,
            signed: null,
            signatureInfo: document.signatureInfo,
          },
        });
      }
    }
  }, [activeModal, changePlanStatusToConsiderModalValues, onChangeToConsiderApply]);

  const handleConfirmModalClose = useCallback(() => {
    setIsConfirmModalVisible(false);
    onClose();
  }, [onClose]);

  const handleChangePlanStatusErrorModalClose = useCallback(() => {
    setIsChangePlanStatusErrorModalVisible(false);
  }, []);

  const handleAfterCloseChangePlanStatusErrorModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <ChangePlanStatusModal
        visible={isChangePlanStatusModalVisible}
        onApply={handleChangePlanStatusApply}
        onCloseModal={handleChangePlanStatusModalClose}
      />
      <ChangePlanStatusToConsiderModal
        visible={isChangePlanStatusToConsiderModalVisible}
        onApply={handleChangePlanStatusToConsiderApply}
        onCloseModal={handleChangePlanStatusToConsiderModalClose}
      />
      <ChangePlanStatusConfirmModal
        visible={isConfirmModalVisible}
        planStatus={planStatus}
        onApply={handleConfirmModalApply}
        onClose={handleConfirmModalClose}
      />
      <ChangePlanStatusErrorModal
        visible={isChangePlanStatusErrorModalVisible}
        text={errorText}
        onClose={handleChangePlanStatusErrorModalClose}
        afterClose={handleAfterCloseChangePlanStatusErrorModalClose}
      />
    </>
  );
};
